import { h } from 'vue'

import { useHLNotification } from '@platform-ui/highrise'
import CPAlert from '@ui/components/CPAlert.vue'
import CPTag from '@ui/components/CPTag.vue'

import type { TreeNode } from '@courses/types-layer/types/category'

export const typeToAlertColor = (type: string): any => {
  const colorMap: Record<string, string> = {
    success: 'green',
    warning: 'orange',
    error: 'red',
    info: 'blue',
  }

  return colorMap[type] ?? 'gray'
}

export function useCourseNotification() {
  const notification = useHLNotification()

  function open(title: string, description: string, type = 'notification') {
    const notificationInstance = notification.create({
      content: () =>
        h(
          CPAlert,
          {
            title: title,
            closable: true,
            id: 'test-alert',
            color: typeToAlertColor(type),
            onClose: () => {
              notificationInstance.destroy()
            },
            type: type as any,
          },
          {
            default: () => description,
          }
        ),
      duration: 5000,
    })
  }

  function destroy() {
    notification.destroyAll()
  }
  return {
    open,
    destroy,
  }
}

// Re-export media URL utilities from canonical location for backward compatibility
export { replaceBucketUrlWithCdnUrl, getBucketName, parseMembershipGCSUrl } from '@common/media-layer/utils/url.utils'

export function numberToAlphabet(number: number) {
  return String.fromCharCode(65 + number)
}

/**
 * Strips all HTML tags and decodes entities, returning normalised plain text.
 * Works on both client (DOMParser) and server (regex fallback).
 */
export function stripHtmlToPlainText(html: string | undefined | null): string {
  if (!html) return ''

  if (typeof window !== 'undefined' && typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    doc.body.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, div, br').forEach(el => el.before(' '))
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
  }

  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

export function removeAndExtractImageFromHtml(html: string) {
  // Check if we're on the client side (browser)
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // Server-side fallback: return original HTML and empty images array
    return {
      html: html || '',
      images: [],
    }
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const imgTags = doc.querySelectorAll('img')
  const images: { src: string; alt: string; dataImageLink: string; dataImageLinkTarget: string }[] = []

  imgTags.forEach(img => {
    images.push({
      src: img.src,
      alt: img.alt,
      dataImageLink: img.getAttribute('data-image-link') || '',
      dataImageLinkTarget: img.getAttribute('data-image-link-target') || '',
    })
    img.remove()
  })

  return {
    html: doc.body.innerHTML || '',
    images: images || [],
  }
}

export function computeNextCategoryId(categoryId: string, categories: Array<{ id: string }>) {
  const categoryIndex = categories.findIndex((category: { id: string }) => category.id === categoryId)
  if (categoryIndex + 1 < categories.length) {
    return categories[categoryIndex + 1]?.id
  }
  return ''
}

export function flattenCategoryTree(nodes: TreeNode[]): { id: string }[] {
  const posts: { id: string }[] = []

  function traverse(node: TreeNode) {
    if (node.type === 'post') {
      posts.push({ id: node.id })
    }

    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        traverse(child)
      }
    }
  }

  for (const node of nodes) {
    traverse(node)
  }

  return posts
}

export function getFirstPost(nodes: TreeNode[]): TreeNode | null {
  const categoryWithPosts = nodes.find(category => category.children?.length > 0)
  if (categoryWithPosts) {
    return categoryWithPosts.children[0]
  }
  return null
}

export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  const floatValue = parseFloat((bytes / Math.pow(k, i)).toFixed(2))
  const unit = sizes[i]
  return `${isNaN(floatValue) ? 'N/A' : floatValue} ${unit || ''}`
}

// Elements that render meaningful content without carrying any text (embeds,
// media, tables, dividers). A description is not empty if any of these exist.
const MEANINGFUL_EMPTY_TEXT_SELECTOR = 'img, iframe, video, audio, source, embed, object, svg, picture, table, hr, canvas'

export function isEmptyHtmlTag(htmlString: string): boolean {
  if (!htmlString || !htmlString?.trim()) return true

  // Check if we're on the client side (browser)
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') {
    // Server-side fallback: return original HTML and empty images array
    return false
  }

  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, 'text/html')
  const body = doc.body

  // Evaluate the whole description, not just the first element: a leading empty
  // `<p></p>` (common editor output) must not hide real content that follows.
  if ((body.textContent || '').trim() !== '') return false

  // Text-less but visible content (an image/iframe/video lesson) still counts.
  if (body.querySelector(MEANINGFUL_EMPTY_TEXT_SELECTOR)) return false

  return true
}

export const truncateString = (str: string, maxLength: number) => {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength) + '...'
}

export function isPropertyParsable(obj: any, prop: string) {
  try {
    JSON.parse(obj[prop])
    return true
  } catch (e) {
    return false
  }
}

export function customTabWithCount(name: string, count: number) {
  return h('span', [
    name,
    h(
      CPTag,
      {
        size: 'sm',
        weight: 'regular',
        bordered: false,
      },
      () => {
        return [count]
      }
    ),
  ])
}
